import axios from 'axios';
import { fetch_form_structure } from '@/services/main_section_service';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  CREATE_MANUAL_VERIFICATION_QUESTION_MAPPINGS_API,
  GET_MANUAL_VERIFICATION_QUESTION_MAPPINGS_API,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  apply_onboarding_selection_change,
  build_empty_state_config,
  build_existing_mappings,
  build_mapping_payload,
  flatten_form_structure_items,
  get_empty_state_route,
  get_manual_question_id_from_row,
  get_onboarding_question_id_from_row,
  get_question_id_value,
  get_question_label_value,
  get_reload_form_ids,
  has_required_reload_form_ids,
  normalize_initial_mappings,
  normalize_question_id_candidate_value,
  parse_mapping_response_data,
  resolve_empty_state,
} from './manual_verification_mapping.helpers';

function get_mapping_error_message(error, fallback_message) {
  const error_message = error?.response?.data?.message || error?.message || fallback_message;
  return error_message;
}

function get_flattened_questions(items) {
  const questions = flatten_form_structure_items(items);
  return questions;
}

export default {
  name: 'ManualVerificationMapping',
  mixins: [alert_mixin],
  emits: ['go_to_manual_verification_form', 'go_to_onboarding_form'],
  props: {
    embedded: { type: Boolean, default: false },
    onboarding_form_id: { type: [String, Number], default: '' },
    onboarding_form_name: { type: String, default: '' },
    manual_verification_form_id: { type: [String, Number], default: '' },
    manual_verification_form_name: { type: String, default: '' },
    initial_mappings: { type: [Array, Object], default: () => ({}) },
    form_link_id: { type: [String, Number], default: '' },
  },
  data: () => ({
    loading: false,
    saving: false,
    loading_existing_mappings: false,
    onboarding_questions: [],
    manual_verification_questions: [],
    mappings: {},
    reload_request_id: 0,
  }),
  watch: {
    onboarding_form_id: { handler() { this.reload_all(); }, immediate: true },
    manual_verification_form_id: { handler() { this.reload_all(); }, immediate: true },
    form_link_id: { handler() { this.reload_all(); }, immediate: true },
    '$route.query': { handler() { this.reload_all(); }, deep: true },
    initial_mappings: { handler() { this.sync_initial_mappings(); }, immediate: true, deep: true },
  },
  computed: {
    resolved_onboarding_form_id() { return this.onboarding_form_id || this.$route?.query?.onboarding_form_id || ''; },
    resolved_onboarding_form_name() { return this.onboarding_form_name || this.$route?.query?.onboarding_form_name || ''; },
    resolved_manual_verification_form_id() { return this.manual_verification_form_id || this.$route?.query?.manual_verification_form_id || ''; },
    resolved_manual_verification_builder_form_id() { return this.$route?.query?.manual_verification_builder_form_id || this.resolved_manual_verification_form_id || ''; },
    resolved_manual_verification_form_name() { return this.manual_verification_form_name || this.$route?.query?.manual_verification_form_name || ''; },
    resolved_form_link_id() { return this.form_link_id || this.$route?.query?.form_link_id || ''; },
    manual_to_onboarding() {
      const next = {};
      Object.keys(this.mappings || {}).forEach((onboarding_id) => {
        const manual_id = Reflect.get(this.mappings || {}, onboarding_id);
        if (manual_id) {
          next[String(manual_id)] = String(onboarding_id);
        }
      });
      return next;
    },
    empty_state() {
      const empty_state = resolve_empty_state({
        loading: this.loading,
        manual_verification_questions: this.manual_verification_questions,
        onboarding_questions: this.onboarding_questions,
        manual_verification_form_name: this.resolved_manual_verification_form_name,
        onboarding_form_name: this.resolved_onboarding_form_name,
      });
      return empty_state;
    },
    mapped_count() {
      const manual_ids = (this.manual_verification_questions || []).map((question_item) => String(question_item.id));
      const mapped_count = manual_ids.filter((manual_id) => Boolean(Reflect.get(this.manual_to_onboarding, manual_id))).length;
      return mapped_count;
    },
  },
  methods: {
    handle_empty_state_action(action_event) {
      const route_config = get_empty_state_route(action_event, {
        onboarding_form_id: this.resolved_onboarding_form_id,
        manual_verification_builder_form_id: this.resolved_manual_verification_builder_form_id,
      }, {
        onboarding_form_name: this.resolved_onboarding_form_name,
        manual_verification_form_name: this.resolved_manual_verification_form_name,
      });

      if (!route_config) {
        this.$emit(action_event);
        return;
      }

      if (!route_config.form_id) {
        return;
      }

      this.$router.push({
        name: 'CreateSections',
        query: {
          form_id: String(route_config.form_id),
          form_name: String(route_config.form_name || ''),
        },
      });
    },

    parse_response_data(response) {
      const parsed_data = parse_mapping_response_data(response);
      return parsed_data;
    },

    build_empty_state(type, form_name, action_event) {
      const empty_state = build_empty_state_config(type, form_name, action_event);
      return empty_state;
    },

    sync_initial_mappings() {
      const next = normalize_initial_mappings(this.initial_mappings);
      this.mappings = next;
    },

    get_manual_question_id_from_row(row) {
      const manual_question_id = get_manual_question_id_from_row(row);
      return manual_question_id;
    },

    get_onboarding_question_id_from_row(row) {
      const onboarding_question_id = get_onboarding_question_id_from_row(row);
      return onboarding_question_id;
    },

    on_change_onboarding_selection(manual_question_id, onboarding_question_id_raw) {
      const next = apply_onboarding_selection_change(this.mappings, manual_question_id, onboarding_question_id_raw);
      this.mappings = next;
    },

    get_question_id(question, fallback_id) {
      const question_id = get_question_id_value(question, fallback_id);
      return question_id;
    },

    normalize_question_id_candidate(value) {
      const normalized_value = normalize_question_id_candidate_value(value);
      return normalized_value;
    },

    get_question_label(question) {
      const label = get_question_label_value(question);
      return label;
    },

    add_flat_question(result, question, fallback_id) {
      const label = this.get_question_label(question);
      if (!label) {
        return;
      }
      result.push({ id: this.get_question_id(question, fallback_id), label });
    },

    flatten_form_structure(items) {
      const flattened_items = get_flattened_questions(items);
      return flattened_items;
    },

    async reload_all() {
      this.reload_request_id += 1;
      const request_id = this.reload_request_id;
      const form_ids = get_reload_form_ids(
        this.resolved_onboarding_form_id,
        this.resolved_manual_verification_builder_form_id,
      );

      if (!has_required_reload_form_ids(form_ids)) {
        this.onboarding_questions = [];
        this.manual_verification_questions = [];
        return;
      }

      this.loading = true;
      await this.load_mapping_questions(form_ids, request_id)
        .catch(() => this.clear_mapping_questions())
        .finally(() => this.clear_reload_loading());
    },

    async load_mapping_questions(form_ids, request_id) {
      const [onboarding_items, manual_items] = await Promise.all([
        fetch_form_structure(form_ids.onboarding_form_id),
        fetch_form_structure(form_ids.manual_verification_builder_form_id),
      ]);
      if (request_id !== this.reload_request_id) return;
      await this.apply_mapping_questions(onboarding_items, manual_items);
    },

    async apply_mapping_questions(onboarding_items, manual_items) {
      this.onboarding_questions = get_flattened_questions(onboarding_items);
      this.manual_verification_questions = get_flattened_questions(manual_items);
      await this.refresh_mapping_selection();
    },

    refresh_mapping_selection() {
      return this.resolved_form_link_id ? this.load_existing_mappings() : this.sync_initial_mappings();
    },

    clear_mapping_questions() {
      this.onboarding_questions = [];
      this.manual_verification_questions = [];
    },

    clear_reload_loading() {
      this.loading = false;
    },

    async load_existing_mappings() {
      const form_link_id = String(this.resolved_form_link_id || '').trim();
      if (!form_link_id) {
        this.sync_initial_mappings();
        return;
      }

      this.loading_existing_mappings = true;
      const response = await this.fetch_existing_mappings(form_link_id)
        .catch((error) => this.handle_existing_mappings_error(error))
        .finally(() => this.clear_existing_mappings_loading());
      if (response) this.apply_existing_mappings(response);
    },

    fetch_existing_mappings(form_link_id) {
      return axios.get(
        GET_MANUAL_VERIFICATION_QUESTION_MAPPINGS_API,
        get_request_config({ params: { form_link_id, only_active: true } }),
      );
    },

    apply_existing_mappings(response) {
      const parsed = this.parse_response_data(response);
      const items = Array.isArray(parsed?.data) ? parsed.data : [];
      this.mappings = build_existing_mappings(
        items,
        get_onboarding_question_id_from_row,
        get_manual_question_id_from_row,
      );
    },

    handle_existing_mappings_error(error) {
      this.mappings = {};
      this.show_error(get_mapping_error_message(error, 'Failed to load manual verification question mappings.'));
      return null;
    },

    clear_existing_mappings_loading() {
      this.loading_existing_mappings = false;
    },

    async save_mappings() {
      const form_link_id = Number(this.resolved_form_link_id) || null;
      if (!form_link_id) {
        this.show_error('Missing form link id. Please assign an onboarding form first.');
        return;
      }

      const mappings = build_mapping_payload(this.mappings);
      if (!mappings.length) {
        this.show_error('Please map at least one question before saving.');
        return;
      }

      this.saving = true;
      const loader = await this.show_loader('Saving mappings...');
      await this.persist_mappings(form_link_id, mappings)
        .catch((error) => this.handle_save_mappings_error(error))
        .finally(() => this.clear_save_mappings_loading(loader));
    },

    async persist_mappings(form_link_id, mappings) {
      const response = await axios.post(
        CREATE_MANUAL_VERIFICATION_QUESTION_MAPPINGS_API,
        { form_link_id, mappings },
        get_request_config(),
      );
      await this.handle_save_mappings_response(response);
    },

    async handle_save_mappings_response(response) {
      const parsed = this.parse_response_data(response);
      if (parsed?.status !== 'success') {
        this.show_error(parsed?.message || 'Failed to save manual verification question mappings.');
        return;
      }
      this.show_success(parsed?.message || 'Manual verification question mappings saved successfully.');
      await this.load_existing_mappings();
    },

    handle_save_mappings_error(error) {
      this.show_error(get_mapping_error_message(error, 'Failed to save manual verification question mappings.'));
    },

    async clear_save_mappings_loading(loader) {
      await this.close_loader(loader);
      this.saving = false;
    },
  },
};
